import {
  UNRESOLVED_MENTION,
  resolveMention,
  type MentionIndex,
  type MentionMatch,
} from '@wlb/shared/utils/mentions';
import {
  isInternalReshareSourceUrl,
  stripReshareAuthorMarker,
} from './reshareAttribution';

export type RichTextSegment =
  | { type: 'text'; value: string }
  | { type: 'mention'; value: string; match: MentionMatch }
  | { type: 'hashtag'; value: string }
  | { type: 'url'; value: string; href: string };

interface ParseRichTextOptions {
  /** Chat keeps protected reshare URLs as clean, clickable post links. */
  showReshareSourceUrls?: boolean;
}

/**
 * Splits plain text into URLs, @mentions, #hashtags, and unchanged text.
 * Both plain and HTML post renderers use this parser so links behave alike.
 */
export function parseRichText(
  text: string,
  mentions?: MentionIndex,
  options: ParseRichTextOptions = {},
): RichTextSegment[] {
  const regex = /(https?:\/\/[^\s<]+|www\.[^\s<]+)|(@[a-zA-Z0-9_.]+)|(#[\p{L}\p{N}_]+)/giu;
  const segments: RichTextSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    if (match[1]) {
      const trailingPunctuation = match[1].match(/[.,!?;:'")\]}]+$/)?.[0] ?? '';
      const value = match[1].slice(0, match[1].length - trailingPunctuation.length);
      if (
        isInternalReshareSourceUrl(value)
        && !options.showReshareSourceUrls
      ) {
        if (trailingPunctuation) {
          segments.push({ type: 'text', value: trailingPunctuation });
        }
        lastIndex = regex.lastIndex;
        continue;
      }
      const visibleValue = stripReshareAuthorMarker(value);
      segments.push({
        type: 'url',
        value: visibleValue,
        href: /^www\./i.test(visibleValue)
          ? `https://${visibleValue}`
          : visibleValue,
      });
      if (trailingPunctuation) {
        segments.push({ type: 'text', value: trailingPunctuation });
      }
    } else if (match[2]) {
      const label = match[2].slice(1);
      segments.push({
        type: 'mention',
        value: label,
        match: mentions ? resolveMention(mentions, label) : UNRESOLVED_MENTION,
      });
    } else if (match[3]) {
      segments.push({ type: 'hashtag', value: match[3] });
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return segments;
}
